import React, { useState } from "react";

import { v4 as uuidv4 } from "uuid";
import {
  AlertTriangle,
  ShieldCheck,
  Dices,
  Gauge,
  Wrench,
  CircleHelp,
  Zap,
  TestTubeDiagonal,
  MountainSnow,
  Radiation,
  Dna,
  Hand,
  Earth,
  ThermometerSun,
  Settings,
  RefreshCw,
  SendToBack,
  MapPinned,
  NotebookTabs,
  Package,
  Users,
  Plus,
  Bot,
  Trash,
  Loader2,
  X,
  Check,
  Table,
} from "lucide-react";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../../components/ui/accordion";
import AutoResizeTextarea from "../../components/AutoResizeTextarea";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import MultipleSelector from "../../components/ui/MultipleSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";

import RiskBadge from "../../components/Risk Matrix/RiskBadge";
import RiskMatrixSelectionModal from "../../components/Risk Matrix/RiskMatrixSelectionModal";
import useRiskMatrix from "../../components/hooks/useRiskMatrix";

import { useToast } from "../../components/hooks/use-toast";
import { useAiModel } from "../../components/hooks/useAiModel";

import { mitigationStatusColors } from "../../helpers/mitigationHelper";
import {
  riskStatusColors,
  calculateRiskRating,
} from "../../helpers/riskHelper";

import functionsService from "../../services/functionsService";
import hazardService from "../../services/hazardService";
import analyticsService from "../../services/analyticsService";
import aiChangeTracking from "../../tracking/AiChangeTracking";

const HazardEditableCard = React.memo(
  ({ project, originalHazard, stopHazardEditing }) => {
    const [hazard, setHazard] = useState(originalHazard);
    const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);
    const [modalRiskType, setModalRiskType] = useState("unmitigated");

    const [isUpdating, setIsUpdating] = useState(false);

    const [isGeneratingConsequences, setIsGeneratingConsequences] =
      useState(false);
    const [isGeneratingMitigations, setIsGeneratingMitigations] =
      useState(false);
    const [isGeneratingSfairpStatement, setIsGeneratingSfairpStatement] =
      useState(false);
    const [isGeneratingSafetyRequirement, setIsGeneratingSafetyRequirement] =
      useState(null);

    const [selectedModel] = useAiModel();
    const { toast } = useToast();

    const { riskMatrix } = useRiskMatrix(project, toast);

    const updateHazard = async () => {
      setIsUpdating(true);

      try {
        const editedHazard = {
          ...hazard,
          unmitigatedRisk: {
            ...hazard.unmitigatedRisk,
            rating: calculateRiskRating(
              hazard.unmitigatedRisk.probability,
              hazard.unmitigatedRisk.severity,
              riskMatrix
            ),
          },
          retainedRisk: {
            ...hazard.retainedRisk,
            rating: calculateRiskRating(
              hazard.retainedRisk.probability,
              hazard.retainedRisk.severity,
              riskMatrix
            ),
          },
        };

        if (hazard.id) {
          await hazardService.updateHazard(project, editedHazard);
        } else {
          await hazardService.createHazard(project, editedHazard);
        }

        stopHazardEditing();
      } catch (error) {
        console.error("Failed to update hazard.", error);

        toast({
          variant: "destructive",
          title: "Failed to update hazard.",
          description: error.message,
        });
      } finally {
        setIsUpdating(false);
      }
    };

    const handleChange = (name, value) => {
      setHazard((prev) => {
        // Update nested property
        if (name.includes(".")) {
          const lhsName = name.split(".")[0];
          const rhsName = name.split(".")[1];

          // Unmitigated risk
          if (lhsName === "unmitigatedRisk") {
            return {
              ...prev,
              unmitigatedRisk: {
                ...prev.unmitigatedRisk,
                [rhsName]: value, // Update the nested property
              },
            };
          }

          // Retained risk
          else if (lhsName === "retainedRisk") {
            return {
              ...prev,
              retainedRisk: {
                ...prev.retainedRisk,
                [rhsName]: value, // Update the nested property
              },
            };
          }
        }

        // Update top-level property
        return { ...prev, [name]: value };
      });
    };

    const generateConsequences = async () => {
      setIsGeneratingConsequences(true);

      try {
        const { success, consequences } =
          await functionsService.generateHazardConsequences(
            project.id,
            hazard.description,
            hazard.type,
            hazard.hazardSource,
            hazard.causes,
            selectedModel
          );

        if (!success) {
          console.error("Failed to generate consequences.");

          toast({
            variant: "destructive",
            title: "Failed to generate consequences.",
          });

          return;
        }

        // Set consequences as array
        setHazard((prev) => ({
          ...prev,
          consequences: [
            ...prev.consequences,
            ...consequences.map((c) => c.consequenceDescription),
          ],
        }));
      } catch (error) {
        console.error("Failed to generate consequences.", error);

        toast({
          variant: "destructive",
          title: "Failed to generate consequences.",
          description: error.message,
        });
      } finally {
        setIsGeneratingConsequences(false);
      }
    };

    const generateMitigations = async () => {
      setIsGeneratingMitigations(true);

      try {
        const { success, mitigations } =
          await functionsService.generateHazardMitigations(
            project.id,

            hazard.description,
            hazard.type,
            hazard.hazardSource,
            hazard.causes,
            hazard.consequences,
            selectedModel
          );

        if (!success) {
          console.error("Failed to generate mitigations.");

          toast({
            variant: "destructive",
            title: "Failed to generate mitigations.",
          });

          return;
        }

        setHazard((prev) => ({
          ...prev,
          mitigations: [...prev.mitigations, ...mitigations],
        }));
      } catch (error) {
        console.error("Failed to generate mitigations.", error);

        toast({
          variant: "destructive",
          title: "Failed to generate mitigations.",
          description: error.message,
        });
      } finally {
        setIsGeneratingMitigations(false);
      }
    };

    const generateSafetyRequirement = async (index) => {
      setIsGeneratingSafetyRequirement(index);

      try {
        const { success, safetyRequirement } =
          await functionsService.generateHazardSafetyRequirement(
            project.id,
            hazard.description,
            hazard.type,
            hazard.mitigations[index].type,
            hazard.mitigations[index].description,
            selectedModel
          );

        if (!success) {
          console.error("Failed to generate safety requirement.");

          toast({
            variant: "destructive",
            title: "Failed to generate safety requirement.",
          });

          return;
        }

        setHazard((prev) => {
          const updatedMitigations = [...prev.mitigations];
          updatedMitigations[index].safetyRequirement = safetyRequirement;
          return { ...prev, mitigations: updatedMitigations };
        });
      } catch (error) {
        console.error("Failed to generate safety requirement.", error);

        toast({
          variant: "destructive",
          title: "Failed to generate safety requirement.",
          description: error.message,
        });
      } finally {
        setIsGeneratingSafetyRequirement(null);
      }
    };

    const handleMitigationChange = (index, field, value) => {
      setHazard((prev) => {
        const updatedMitigations = [...prev.mitigations];

        updatedMitigations[index] = {
          ...updatedMitigations[index],
          [field]: value,
        };

        return { ...prev, mitigations: updatedMitigations };
      });
    };

    const generateSfairpStatement = async () => {
      setIsGeneratingSfairpStatement(true);

      try {
        const { success, sfairpStatement } =
          await functionsService.generateHazardSFAIRP(
            project.id,
            hazard.description,
            hazard.type,
            hazard.hazardSource,
            hazard.causes,
            hazard.consequences,
            hazard.unmitigatedRisk,
            hazard.mitigations,
            hazard.retainedRisk,
            selectedModel
          );

        if (!success) {
          console.error("Failed to generate SFAIRP statement.");

          toast({
            variant: "destructive",
            title: "Failed to generate SFAIRP statement.",
          });
          return;
        }

        setHazard((prev) => ({
          ...prev,
          sfairpStatement: prev.sfairpStatement
            ? `${prev.sfairpStatement}\n\n${sfairpStatement}`
            : sfairpStatement,
        }));
      } catch (error) {
        console.error("Failed to generate SFAIRP statement:", error);

        toast({
          variant: "destructive",
          title: "Failed to generate SFAIRP statement.",
          description: error.message,
        });
      } finally {
        setIsGeneratingSfairpStatement(false);
      }
    };

    const renderHazardTypeIcon = (hazardType) => {
      let icon;

      switch (hazardType) {
        case "Mechanical":
          icon = <Wrench className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        case "Electrical":
          icon = <Zap className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        case "Chemical":
          icon = <TestTubeDiagonal className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        case "Geological":
          icon = <MountainSnow className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        case "Biological":
          icon = <Dna className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        case "Radiation":
          icon = <Radiation className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        case "Human-made":
          icon = <Hand className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        case "Environmental":
          icon = <Earth className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        case "Thermal":
          icon = <ThermometerSun className="h-4 w-4 mr-2 cursor-pointer" />;
          break;

        default:
          icon = <CircleHelp className="h-4 w-4 mr-2 cursor-pointer" />;
          break;
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>{icon}</TooltipTrigger>
          <TooltipContent>
            <p>Hazard Type: {hazardType}</p>
          </TooltipContent>
        </Tooltip>
      );
    };

    const handleMatrixCellSelection = (probability, severity) => {
      if (modalRiskType === "unmitigated") {
        handleChange("unmitigatedRisk.probability", probability);
        handleChange("unmitigatedRisk.severity", severity);
      } else if (modalRiskType === "retained") {
        handleChange("retainedRisk.probability", probability);
        handleChange("retainedRisk.severity", severity);
      }
    };

    const openRiskMatrixModal = (riskType) => {
      setModalRiskType(riskType);
      setIsRiskMatrixModalOpen(true);
    };

    return (
      <Card className="border-4 p-1">
        <CardHeader className="bg-gray-50 p-2">
          <div className="w-full">
            <div className="grid">
              <CardTitle>
                {hazard.id ? "Edit hazard" : "Create hazard"}
              </CardTitle>

              <div className="flex justify-end">
                <Button
                  className="min-w-[135px]"
                  variant="outline"
                  onClick={stopHazardEditing}
                  disabled={isUpdating}
                >
                  <X className="h-4 w-4 mr-2" />
                  <span>Cancel Changes</span>
                </Button>

                <Button
                  className="ml-4 min-w-[135px]"
                  onClick={updateHazard}
                  disabled={
                    isUpdating ||
                    !hazard.type ||
                    !hazard.hazardSource ||
                    !hazard.description
                  }
                >
                  {isUpdating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      <span>Publishing...</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      <span>Publish Changes</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>

        <Card className="w-full shadow-sm">
          <div
            className={`sticky top-0 z-30 ${riskStatusColors[hazard.riskStatus]} ${
              hazard.riskStatus === "Cancelled" ? "opacity-50" : ""
            }`}
          >
            <CardHeader className="p-2">
              <CardTitle className="flex justify-between items-center">
                <div className="flex-shrink-0 flex flex-col mr-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Select
                        value={hazard.type}
                        onValueChange={(value) => {
                          const oldValue = hazard.type;
                          
                          // Track user edits to AI-generated hazard type
                          aiChangeTracking.trackHazardTypeEdit(hazard, oldValue, value);
                          
                          handleChange("type", value);
                        }}
                        disabled={isUpdating}
                      >
                        <SelectTrigger compact>
                          <SelectValue placeholder="Select Hazard Type" />
                        </SelectTrigger>

                        <SelectContent className="z-[10001]">
                          {[
                            "Mechanical",
                            "Electrical",
                            "Chemical",
                            "Geological",
                            "Biological",
                            "Radiation",
                            "Human-made",
                            "Environmental",
                            "Thermal",
                          ].map((status) => (
                            <SelectItem key={status} value={status}>
                              <div className="flex items-center mr-1">
                                {renderHazardTypeIcon(status)}
                                {status}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TooltipTrigger>

                    <TooltipContent>Hazard Type</TooltipContent>
                  </Tooltip>
                </div>

                <div className="flex-grow flex flex-col">
                  <span className="text-base font-bold">
                    {hazard.id ? `${hazard.hazardId}` : "Automatic ID"}
                  </span>

                  <div className="flex items-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Input
                          compact
                          value={hazard.hazardSource}
                          onChange={(e) => {
                            const oldValue = hazard.hazardSource;
                            const newValue = e.target.value;
                            
                            // Track user edits to AI-generated hazard source
                            aiChangeTracking.trackHazardSourceEdit(hazard, oldValue, newValue);
                            
                            handleChange("hazardSource", newValue);
                          }}
                          placeholder="Example: Exposed Wires, Equipment Failure, etc."
                          className="max-w-[150px] text-base text-neutral-800 mt-1"
                          disabled={isUpdating}
                        />
                      </TooltipTrigger>

                      <TooltipContent>Hazard Source</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Input
                          compact
                          value={hazard.description}
                          onChange={(e) => {
                            const oldValue = hazard.description;
                            const newValue = e.target.value;
                            
                            // Track user edits to AI-generated hazard descriptions
                            aiChangeTracking.trackHazardDescriptionEdit(hazard, oldValue, newValue);
                            
                            handleChange("description", newValue);
                          }}
                          placeholder="Example: Exposed wires in maintenance access panel..."
                          className="text-base text-gray-500 mx-1 mt-1"
                          disabled={isUpdating}
                        />
                      </TooltipTrigger>

                      <TooltipContent>Description</TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                <div className="flex items-center space-x-1 mt-7">
                  <Select
                    onValueChange={(value) => handleChange("riskStatus", value)}
                    defaultValue={hazard.riskStatus}
                    disabled={isUpdating}
                  >
                    <SelectTrigger className="w-[120px] bg-white text-xs h-8">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>

                    <SelectContent>
                      {Object.keys(riskStatusColors).map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardTitle>
            </CardHeader>
          </div>

          <CardContent className="bg-white p-2">
            <div className="grid grid-cols-6 gap-1 text-sm mb-4">
              <div className="bg-gray-50 p-2 rounded">
                <div className="flex items-center space-x-1">
                  <Settings className="h-4 w-4 mr-1" />
                  <span className="font-semibold text-gray-600">Systems: </span>
                </div>

                <MultipleSelector
                  compact
                  hideClearAllButton
                  hidePlaceholderWhenSelected
                  className="mt-2"
                  defaultOptions={
                    project.projectMetadata?.systems?.map((system) => {
                      return { label: system.name, value: system.name };
                    }) || []
                  }
                  value={
                    hazard.systems.map((system) => {
                      return { label: system, value: system };
                    }) || []
                  }
                  onChange={(value) =>
                    handleChange(
                      "systems",
                      value.map((x) => x.value)
                    )
                  }
                  placeholder="Select systems..."
                  disabled={isUpdating}
                />
              </div>

              <div className="bg-gray-50 p-2 rounded">
                <div className="flex items-center space-x-1">
                  <SendToBack className="h-4 w-4 mr-1" />

                  <span className="font-semibold text-gray-600">
                    Operating Modes:{" "}
                  </span>
                </div>

                <MultipleSelector
                  compact
                  hideClearAllButton
                  hidePlaceholderWhenSelected
                  className="mt-2"
                  defaultOptions={
                    project.projectMetadata?.operatingModes.map(
                      (operatingMode) => {
                        return {
                          label: operatingMode,
                          value: operatingMode,
                        };
                      }
                    ) || []
                  }
                  value={
                    hazard.operatingModes.map((operatingMode) => {
                      return { label: operatingMode, value: operatingMode };
                    }) || []
                  }
                  onChange={(value) =>
                    handleChange(
                      "operatingModes",
                      value.map((x) => x.value)
                    )
                  }
                  placeholder="Select operating modes..."
                  disabled={isUpdating}
                />
              </div>

              <div className="bg-gray-50 p-2 rounded">
                <div className="flex items-center space-x-1">
                  <MapPinned className="h-4 w-4 mr-1" />
                  <span className="font-semibold text-gray-600">
                    Locations:{" "}
                  </span>
                </div>

                <MultipleSelector
                  compact
                  hideClearAllButton
                  hidePlaceholderWhenSelected
                  className="mt-2"
                  defaultOptions={
                    project.projectMetadata?.locations.map((location) => {
                      return { label: location, value: location };
                    }) || []
                  }
                  value={
                    hazard.locations.map((location) => {
                      return { label: location, value: location };
                    }) || []
                  }
                  onChange={(value) =>
                    handleChange(
                      "locations",
                      value.map((x) => x.value)
                    )
                  }
                  placeholder="Select location(s)..."
                  disabled={isUpdating}
                />
              </div>

              <div className="bg-gray-50 p-2 rounded">
                <div className="flex items-center space-x-1">
                  <RefreshCw className="h-4 w-4 mr-1" />
                  <span className="font-semibold text-gray-600">
                    Project Lifecycles:{" "}
                  </span>
                </div>

                <MultipleSelector
                  compact
                  hideClearAllButton
                  hidePlaceholderWhenSelected
                  className="mt-2"
                  defaultOptions={
                    project.projectMetadata?.projectLifecycles.map(
                      (projectLifecycle) => {
                        return {
                          label: projectLifecycle,
                          value: projectLifecycle,
                        };
                      }
                    ) || []
                  }
                  value={
                    hazard.projectLifecycles.map((projectLifecycle) => {
                      return {
                        label: projectLifecycle,
                        value: projectLifecycle,
                      };
                    }) || []
                  }
                  onChange={(value) =>
                    handleChange(
                      "projectLifecycles",
                      value.map((x) => x.value)
                    )
                  }
                  placeholder="Select project lifecycle(s)..."
                  disabled={isUpdating}
                />
              </div>

              <div className="bg-gray-50 p-2 rounded">
                <div className="flex items-center space-x-1">
                  <NotebookTabs className="h-4 w-4 mr-1" />
                  <span className="font-semibold text-gray-600">
                    Disciplines:{" "}
                  </span>
                </div>

                <MultipleSelector
                  compact
                  hideClearAllButton
                  hidePlaceholderWhenSelected
                  className="mt-2"
                  defaultOptions={
                    project.projectMetadata?.disciplines.map((discipline) => {
                      return { label: discipline, value: discipline };
                    }) || []
                  }
                  value={
                    hazard.disciplines.map((discipline) => {
                      return { label: discipline, value: discipline };
                    }) || []
                  }
                  onChange={(value) =>
                    handleChange(
                      "disciplines",
                      value.map((x) => x.value)
                    )
                  }
                  placeholder="Select discipline(s)..."
                  disabled={isUpdating}
                />
              </div>

              <div className="bg-gray-50 p-2 rounded">
                <div className="flex items-center space-x-1">
                  <Package className="h-4 w-4 mr-1" />
                  <span className="font-semibold text-gray-600">
                    Drawing Packages:{" "}
                  </span>
                </div>

                <MultipleSelector
                  compact
                  hideClearAllButton
                  hidePlaceholderWhenSelected
                  className="mt-2"
                  defaultOptions={
                    project.projectMetadata?.drawingPackages.map(
                      (drawingPackage) => {
                        return {
                          label: drawingPackage,
                          value: drawingPackage,
                        };
                      }
                    ) || []
                  }
                  value={
                    hazard.drawingPackages.map((drawingPackage) => {
                      return {
                        label: drawingPackage,
                        value: drawingPackage,
                      };
                    }) || []
                  }
                  onChange={(value) =>
                    handleChange(
                      "drawingPackages",
                      value.map((x) => x.value)
                    )
                  }
                  placeholder="Select drawing package(s)..."
                  disabled={isUpdating}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-3 gap-2 text-sm">
              <div className="p-2 rounded bg-gray-100">
                <div className="grid grid-cols-2">
                  <div className="col-span-2 sm:col-span-1">
                    <div className="flex items-center space-x-1">
                      <AlertTriangle className="h-4 w-4 mr-1 mt-1" />
                      <h3 className="font-semibold mt-1">Unmitigated Risk</h3>
                    </div>
                  </div>

                  <div className="flex justify-end col-span-1">
                    <div className="flex items-center space-x-1 mb-1">
                      <RiskBadge
                        riskMatrixName={project.riskMatrix}
                        probability={hazard.unmitigatedRisk.probability}
                        severity={hazard.unmitigatedRisk.severity}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center mb-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center space-x-1 cursor-pointer">
                        <Dices className="h-4 w-4" />

                        <Select
                          value={hazard.unmitigatedRisk.probability}
                          onValueChange={(value) =>
                            handleChange("unmitigatedRisk.probability", value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger compact className="max-w-[120px]">
                            <SelectValue placeholder="Select probability" />
                          </SelectTrigger>

                          <SelectContent className="z-[10001]">
                            {riskMatrix?.probabilities?.map((prob) => (
                              <SelectItem key={prob} value={prob}>
                                {prob}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>

                    <TooltipContent>
                      <p>Probability</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center space-x-1 ml-4 cursor-pointer">
                        <Gauge className="h-4 w-4" />

                        <Select
                          value={hazard.unmitigatedRisk.severity}
                          onValueChange={(value) =>
                            handleChange("unmitigatedRisk.severity", value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger compact className="max-w-[120px]">
                            <SelectValue placeholder="Select severity" />
                          </SelectTrigger>

                          <SelectContent className="z-[10001]">
                            {riskMatrix?.severities?.map((sev) => (
                              <SelectItem key={sev} value={sev}>
                                {sev}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>

                    <TooltipContent>
                      <p>Severity</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Table
                        className="h-6 w-6 ml-4 cursor-pointer transition-all duration-200 hover:text-accent-foreground hover:scale-125 focus:text-accent-foreground active:scale-95"
                        onClick={() => openRiskMatrixModal("unmitigated")}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Select from Risk Matrix</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className="p-2 rounded bg-gray-100">
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4 mr-1 mb-1" />
                  <h3 className="font-semibold mb-1">Affected Users</h3>
                </div>

                <div className="flex items-center space-x-1">
                  <MultipleSelector
                    compact
                    hideClearAllButton
                    hidePlaceholderWhenSelected
                    defaultOptions={
                      project.projectMetadata?.users.map((user) => {
                        return { label: user, value: user };
                      }) || []
                    }
                    value={
                      hazard.users.map((user) => {
                        return { label: user, value: user };
                      }) || []
                    }
                    onChange={(value) =>
                      handleChange(
                        "users",
                        value.map((x) => x.value)
                      )
                    }
                    placeholder="Select affected user(s)..."
                    disabled={isUpdating}
                  />
                </div>
              </div>

              <div className="p-2 rounded bg-gray-100">
                <div className="grid grid-cols-2">
                  <div className="col-span-2 sm:col-span-1">
                    <div className="flex items-center space-x-1">
                      <ShieldCheck className="h-4 w-4 mr-1 mt-1" />
                      <h3 className="font-semibold mt-1">Retained Risk</h3>
                    </div>
                  </div>

                  <div className="flex justify-end col-span-1">
                    <div className="flex items-center space-x-1 mb-1">
                      <RiskBadge
                        riskMatrixName={project.riskMatrix}
                        probability={hazard.retainedRisk.probability}
                        severity={hazard.retainedRisk.severity}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center mb-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center space-x-1 cursor-pointer">
                        <Dices className="h-4 w-4" />

                        <Select
                          value={hazard.retainedRisk.probability}
                          onValueChange={(value) =>
                            handleChange("retainedRisk.probability", value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger compact className="max-w-[120px]">
                            <SelectValue placeholder="Select probability" />
                          </SelectTrigger>

                          <SelectContent className="z-[10001]">
                            {riskMatrix?.probabilities?.map((prob) => (
                              <SelectItem key={prob} value={prob}>
                                {prob}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>

                    <TooltipContent>
                      <p>Probability</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center space-x-1 ml-4 cursor-pointer">
                        <Gauge className="h-4 w-4" />

                        <Select
                          value={hazard.retainedRisk.severity}
                          onValueChange={(value) =>
                            handleChange("retainedRisk.severity", value)
                          }
                          disabled={isUpdating}
                        >
                          <SelectTrigger compact className="max-w-[120px]">
                            <SelectValue placeholder="Select severity" />
                          </SelectTrigger>

                          <SelectContent className="z-[10001]">
                            {riskMatrix?.severities?.map((sev) => (
                              <SelectItem key={sev} value={sev}>
                                {sev}
                              </SelectItem>
                            )) || []}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>

                    <TooltipContent>
                      <p>Severity</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Table
                        className="h-6 w-6 ml-4 cursor-pointer transition-all duration-200 hover:text-accent-foreground hover:scale-125 focus:text-accent-foreground active:scale-95"
                        onClick={() => openRiskMatrixModal("retained")}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Select from Risk Matrix</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1">
              <div className="bg-gray-50 p-2 rounded mb-1">
                <div className="grid grid-cols-2">
                  <div className="col-span-2 sm:col-span-1">
                    <h3 className="text-sm font-semibold text-gray-700 ml-2 my-2">
                      Causes
                    </h3>
                  </div>

                  <div className="flex justify-end col-span-1">
                    <div className="flex items-center space-x-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              setHazard((prev) => ({
                                ...prev,
                                causes: [...prev.causes, ""],
                              }));
                            }}
                            disabled={isUpdating}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>

                        <TooltipContent>
                          <p>Add cause</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {hazard.causes.length > 0 ? (
                  <div className="space-y-2">
                    {hazard.causes.map((cause, index) => (
                      <div key={index} className="flex flex-col space-y-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-full">
                            <Input
                              className="w-full"
                              value={cause}
                              onChange={(e) => {
                                const oldValue = cause;
                                const newValue = e.target.value;
                                const newCauses = [...hazard.causes];
                                newCauses[index] = newValue;
                                
                                // Track user edits to AI-generated causes
                                aiChangeTracking.trackCauseEdit(hazard, oldValue, newValue);
                                
                                handleChange("causes", newCauses);
                              }}
                              placeholder="Enter cause description"
                              disabled={isUpdating}
                            />
                          </div>

                          <div className="flex items-center space-x-2">
                            <Button
                              size="icon"
                              className="h-6 w-6"
                              variant="ghost"
                              onClick={() => {
                                const newCauses = hazard.causes.filter(
                                  (_, i) => i !== index
                                );
                                handleChange("causes", newCauses);
                              }}
                              disabled={isUpdating}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic text-sm ml-2">
                    No causes identified.
                  </p>
                )}
              </div>

              <div className="bg-gray-50 p-2 rounded">
                <div className="grid grid-cols-2">
                  <div className="col-span-2 sm:col-span-1">
                    <h3 className="text-sm font-semibold text-gray-700 ml-2 my-2">
                      Consequences
                    </h3>
                  </div>

                  <div className="flex justify-end col-span-1">
                    <div className="flex items-center space-x-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              setHazard((prev) => ({
                                ...prev,
                                consequences: [...prev.consequences, ""],
                              }));
                            }}
                            disabled={isUpdating}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>

                        <TooltipContent>
                          <p>Add consequence</p>
                        </TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            className="h-6 w-6"
                            onClick={generateConsequences}
                            disabled={
                              !hazard.description ||
                              isGeneratingConsequences ||
                              isUpdating
                            }
                          >
                            {isGeneratingConsequences ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Bot className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>

                        <TooltipContent>
                          <p>Generate consequences</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {hazard.consequences.length > 0 ? (
                  <div className="space-y-2">
                    {hazard.consequences.map((consequence, index) => (
                      <div key={index} className="flex flex-col space-y-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-full">
                            <Input
                              className="w-full"
                              value={consequence}
                              onChange={(e) => {
                                const oldValue = consequence;
                                const newValue = e.target.value;
                                const newConsequences = [...hazard.consequences];
                                newConsequences[index] = newValue;
                                
                                // Track user edits to AI-generated consequences
                                aiChangeTracking.trackConsequenceEdit(hazard, oldValue, newValue);
                                
                                handleChange("consequences", newConsequences);
                              }}
                              placeholder="Enter consequence description"
                              disabled={isUpdating}
                            />
                          </div>

                          <div className="flex items-center space-x-2">
                            <Button
                              size="icon"
                              className="h-6 w-6"
                              variant="ghost"
                              onClick={() => {
                                const updatedConsequences =
                                  hazard.consequences.filter(
                                    (_, i) => i !== index
                                  );
                                handleChange(
                                  "consequences",
                                  updatedConsequences
                                );
                              }}
                              disabled={isUpdating}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic text-sm ml-2">
                    No consequences identified.
                  </p>
                )}
              </div>

              <div className="bg-gray-50 p-2 rounded">
                <div className="grid grid-cols-2">
                  <div className="col-span-2 sm:col-span-1">
                    <h3 className="text-sm font-semibold text-gray-700 ml-2 my-2">
                      Mitigations
                    </h3>
                  </div>

                  <div className="flex justify-end col-span-1">
                    <div className="flex items-center space-x-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => {
                              setHazard((prev) => ({
                                ...prev,
                                mitigations: [
                                  ...prev.mitigations,
                                  {
                                    id: uuidv4(),
                                    mitigationId: "",
                                    type: "Elimination",
                                    description: "",
                                    owner: "",
                                    status: "Proposed",
                                    dueDate: "",
                                    safetyRequirement: "",
                                    evidence: "",
                                  },
                                ],
                              }));
                            }}
                            disabled={isUpdating}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>

                        <TooltipContent>
                          <p>Add mitigation</p>
                        </TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            className="h-6 w-6"
                            onClick={generateMitigations}
                            disabled={
                              !hazard.description ||
                              !hazard.consequences ||
                              isGeneratingMitigations ||
                              isUpdating
                            }
                          >
                            {isGeneratingMitigations ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Bot className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>

                        <TooltipContent>
                          <p>Generate mitigations</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {hazard.mitigations.length > 0 ? (
                  <div className="space-y-2">
                    {hazard.mitigations.map((mitigation, index) => (
                      <div key={index} className="flex flex-col space-y-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-full">
                            <Accordion
                              type="single"
                              collapsible
                              className="w-full mb-2"
                              key={index}
                            >
                              <AccordionItem
                                value={`item-${index}`}
                                className={`${
                                  mitigationStatusColors[mitigation.status] ||
                                  "bg-gray-50"
                                } rounded-md overflow-hidden`}
                              >
                                <AccordionTrigger className="text-sm py-2 px-3 hover:no-underline hover:bg-opacity-80">
                                  <div className="flex flex-col items-start w-full text-left">
                                    <Select
                                      value={mitigation.type}
                                      onValueChange={(value) =>
                                        handleMitigationChange(
                                          index,
                                          "type",
                                          value
                                        )
                                      }
                                      disabled={isUpdating}
                                    >
                                      <SelectTrigger
                                        compact
                                        className="max-w-[200px]"
                                      >
                                        <SelectValue placeholder="Select type" />
                                      </SelectTrigger>

                                      <SelectContent className="z-[10001]">
                                        {[
                                          "Elimination",
                                          "Substitution",
                                          "Isolation",
                                          "Engineering",
                                          "Administrative",
                                          "Personal Protective Equipment",
                                        ].map((type) => (
                                          <SelectItem key={type} value={type}>
                                            {type}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>

                                    <Textarea
                                      value={mitigation.description}
                                      onChange={(e) => {
                                        const oldValue = mitigation.description;
                                        const newValue = e.target.value;
                                        
                                        // Track user edits to AI-generated mitigation descriptions
                                        if (hazard.aiGenerated && hazard.aiGenerated.mitigations && 
                                            mitigation.aiGenerated && oldValue !== newValue) {
                                          analyticsService.trackFeatureUsage('edited_ai_content', {
                                            content_type: 'mitigation_description',
                                            hazard_id: hazard.id || 'new',
                                            mitigation_id: mitigation.id,
                                            mitigation_type: mitigation.type,
                                            original_length: oldValue.length,
                                            new_length: newValue.length,
                                            edit_distance: Math.abs(newValue.length - oldValue.length)
                                          });
                                        }
                                        
                                        handleMitigationChange(
                                          index,
                                          "description",
                                          newValue
                                        );
                                      }}
                                      className="min-h-[80px] resize-none mt-2"
                                      placeholder="Mitigation description..."
                                      disabled={isUpdating}
                                    />
                                  </div>
                                </AccordionTrigger>

                                <AccordionContent className="px-3 py-2 bg-white bg-opacity-60">
                                  <div className="text-xs">
                                    <div className="flex items-center mb-1">
                                      <strong className="mr-2">Owner:</strong>

                                      <Select
                                        value={mitigation.owner}
                                        onValueChange={(value) =>
                                          handleMitigationChange(
                                            index,
                                            "owner",
                                            value
                                          )
                                        }
                                        disabled={isUpdating}
                                      >
                                        <SelectTrigger
                                          compact
                                          textSize="text-xs"
                                          className="max-w-[140px]"
                                        >
                                          <SelectValue placeholder="Select owner" />
                                        </SelectTrigger>

                                        <SelectContent className="z-[10001]">
                                          {project.projectMetadata?.mitigationOwners?.map(
                                            (owner) => (
                                              <SelectItem
                                                key={owner}
                                                value={owner}
                                              >
                                                {owner}
                                              </SelectItem>
                                            )
                                          ) || []}
                                        </SelectContent>
</Select>
                                    </div>

                                    <div className="flex items-center mb-1">
                                      <strong className="mr-2">Status:</strong>

                                      <Select
                                        value={mitigation.status}
                                        onValueChange={(value) =>
                                          handleMitigationChange(
                                            index,
                                            "status",
                                            value
                                          )
                                        }
                                        disabled={isUpdating}
                                      >
                                        <SelectTrigger
                                          compact
                                          textSize="text-xs"
                                          className="max-w-[140px]"
                                        >
                                          <SelectValue placeholder="Select status" />
                                        </SelectTrigger>

                                        <SelectContent className="z-[10001]">
                                          {[
                                            "Proposed",
                                            "Implemented",
                                            "Transferred",
                                            "Rejected",
                                          ].map((status) => (
                                            <SelectItem
                                              key={status}
                                              value={status}
                                            >
                                              {status}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    <div className="flex items-center mb-1">
                                      <strong className="mr-2">
                                        Due Date:
                                      </strong>

                                      <Input
                                        compact
                                        className="text-xs max-w-[120px]"
                                        type="date"
                                        value={mitigation.dueDate}
                                        onChange={(e) =>
                                          handleMitigationChange(
                                            index,
                                            "dueDate",
                                            e.target.value
                                          )
                                        }
                                        disabled={isUpdating}
                                      />
                                    </div>

                                    <div className="flex items-center mb-1">
                                      <strong className="mr-2">
                                        Safety Requirement:
                                      </strong>

                                      <div className="flex items-center">
                                        <Input
                                          compact
                                          className="text-xs max-w-[140px]"
                                          value={mitigation.safetyRequirement}
                                          onChange={(e) => {
                                            const oldValue = mitigation.safetyRequirement;
                                            const newValue = e.target.value;
                                            
                                            // Track user edits to AI-generated safety requirements
                                            if (hazard.aiGenerated && 
                                                hazard.aiGenerated.safetyRequirements && 
                                                mitigation.aiGenerated && 
                                                oldValue !== newValue) {
                                              analyticsService.trackFeatureUsage('edited_ai_content', {
                                                content_type: 'safety_requirement',
                                                hazard_id: hazard.id || 'new',
                                                mitigation_id: mitigation.id,
                                                original_length: oldValue.length,
                                                new_length: newValue.length,
                                                edit_distance: Math.abs(newValue.length - oldValue.length)
                                              });
                                            }
                                            
                                            handleMitigationChange(
                                              index,
                                              "safetyRequirement",
                                              e.target.value
                                            );
                                          }}
                                          disabled={isUpdating}
                                        />
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              size="icon"
                                              className="h-6 w-6 ml-1"
                                              variant="ghost"
                                              onClick={() =>
                                                generateSafetyRequirement(index)
                                              }
                                              disabled={
                                                !mitigation.description ||
                                                isGeneratingSafetyRequirement ===
                                                  index ||
                                                isUpdating
                                              }
                                            >
                                              {isGeneratingSafetyRequirement ===
                                              index ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                              ) : (
                                                <Bot className="h-4 w-4" />
                                              )}
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Generate Safety Requirement</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </div>
                                    </div>

                                    <div className="flex items-center mb-1">
                                      <strong className="mr-2">
                                        Evidence:
                                      </strong>

                                      <Input
                                        compact
                                        className="text-xs max-w-[140px]"
                                        value={mitigation.evidence}
                                        onChange={(e) =>
                                          handleMitigationChange(
                                            index,
                                            "evidence",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Add evidence link..."
                                        disabled={isUpdating}
                                      />
                                    </div>
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Button
                              size="icon"
                              className="h-6 w-6"
                              variant="ghost"
                              onClick={() => {
                                const updatedMitigations =
                                  hazard.mitigations.filter(
                                    (_, i) => i !== index
                                  );
                                handleChange("mitigations", updatedMitigations);
                              }}
                              disabled={isUpdating}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic text-sm ml-2">
                    No mitigations proposed.
                  </p>
                )}
              </div>
            </div>
          </CardContent>

          <CardFooter className="bg-gray-50 p-2">
            <Accordion type="single" collapsible className="w-full mx-2">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-sm">
                  Safety Statement and Residual Risk
                </AccordionTrigger>

                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mx-2">
                    <div>
                      <div className="grid grid-cols-2 mb-2">
                        <div className="col-span-2 sm:col-span-1">
                          <h3 className="font-semibold text-gray-700">
                            Safety Statement
                          </h3>
                        </div>

                        <div className="flex justify-end col-span-1">
                          <div className="flex items-center space-x-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={generateSfairpStatement}
                                  disabled={
                                    isGeneratingSfairpStatement || isUpdating
                                  }
                                >
                                  {isGeneratingSfairpStatement ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Bot className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>

                              <TooltipContent>
                                <p>Generate Safety Statement</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      <AutoResizeTextarea
                        value={hazard.sfairpStatement}
                        onChange={(e) => {
                          const oldValue = hazard.sfairpStatement;
                          const newValue = e.target.value;
                          
                          // Track user edits to AI-generated SFAIRP statements
                          if (hazard.aiGenerated && hazard.aiGenerated.sfairpStatement && 
                              oldValue !== newValue) {
                            analyticsService.trackFeatureUsage('edited_ai_content', {
                              content_type: 'sfairp_statement',
                              hazard_id: hazard.id || 'new',
                              original_length: oldValue.length,
                              new_length: newValue.length,
                              edit_distance: Math.abs(newValue.length - oldValue.length)
                            });
                          }
                          
                          handleChange("sfairpStatement", newValue);
                        }}
                        placeholder="Enter SFAIRP statement..."
                        disabled={isGeneratingSfairpStatement || isUpdating}
                      />
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3 text-gray-700">
                        Description of Residual Risk
                      </h3>

                      <AutoResizeTextarea
                        value={hazard.residualRiskDescription}
                        onChange={(e) =>
                          handleChange(
                            "residualRiskDescription",
                            e.target.value
                          )
                        }
                        placeholder="Enter Residual Risk description..."
                        disabled={isUpdating}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardFooter>
        </Card>

        <RiskMatrixSelectionModal
          isOpen={isRiskMatrixModalOpen}
          setIsOpen={setIsRiskMatrixModalOpen}
          riskMatrix={riskMatrix}
          onSelectCell={handleMatrixCellSelection}
          title={`Select ${
            modalRiskType === "unmitigated" ? "Unmitigated" : "Retained"
          } Risk Values`}
          description="Click on a cell in the risk matrix to select both probability and severity values."
          riskType={modalRiskType}
        />
      </Card>
    );
  }
);

export default HazardEditableCard;
